import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Plus, Kanban, UserCircle, MagnifyingGlass, Archive, SquaresFour, ChartBar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useT } from '@/i18n/LanguageContext';
import { plannerApi } from './plannerApi';
import { PRIORITY_BAR, PROJECT_FILTERS, ganttOrder } from './plannerConstants';
import ProjectCard from './components/ProjectCard';
import ProjectFormDialog from './components/ProjectFormDialog';
import GanttChart from './components/GanttChart';

// Batch 3 · iter_153 — My Projects. Backend-filtered participation view.
// Rule (Q1=c): user is a member iff owner OR project-member OR task-member.
// Create-button: any authenticated user (Q2=b).

function roleOf(p) {
  if (p.is_owner) return 'owner';
  if (p.is_member) return 'member';
  return 'task_member';
}

function StatusChip({ value, count, active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      data-testid={`myproj-filter-${value}`}
      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors flex items-center gap-1.5 ${
        active ? 'bg-indigo-600 text-white shadow' : 'bg-white border border-zinc-200 text-zinc-600 hover:bg-zinc-50'
      }`}
    >
      {label}
      <span className={`inline-flex items-center justify-center min-w-[18px] px-1 h-4 rounded-full text-[10px] font-bold ${active ? 'bg-white/20' : 'bg-zinc-100 text-zinc-500'}`}>
        {count}
      </span>
    </button>
  );
}

// The 完了済み chip reads `is_finished` (canonical, stored). It used to count
// `status === 'completed'`, a value nothing ever wrote — so the chip showed 0
// while the project screen listed finished projects.
const matchesChip = (p, chip) => {
  if (chip === 'finished') return p.is_finished === true;
  if (p.is_finished === true) return false;
  return (p.status || 'active') === chip;
};

export default function MyProjects() {
  const { t } = useT();
  const navigate = useNavigate();
  const ctx = useOutletContext();
  const [data, setData] = useState({ employee_linked: true, projects: [] });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [includeArchived, setIncludeArchived] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [people, setPeople] = useState([]);
  const [view, setView] = useState(() => localStorage.getItem('myproj_view') || 'cards');
  const switchView = (v) => { setView(v); localStorage.setItem('myproj_view', v); };

  const load = useCallback(() => {
    setLoading(true);
    setErr(null);
    // ALWAYS fetch finished projects. The 完了済み chip has to be able to show
    // a truthful count, and it read 0 while a project was finished when the
    // fetch itself was gated behind the 完了も表示 toggle. Hiding is done
    // client-side in `visible` instead.
    plannerApi.myProjects({ include_archived: true })
      .then((d) => setData(d))
      .catch((e) => setErr(e?.response?.data?.detail || t('planner.error')))
      .finally(() => setLoading(false));
  }, [t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { plannerApi.people().then(setPeople).catch(() => {}); }, []);

  // Memoise so the `|| []` fallback doesn't create a fresh array reference
  // on every render (which would invalidate the useMemo deps below and
  // trigger react-hooks/exhaustive-deps errors under CI=1).
  const projects = useMemo(() => data.projects || [], [data.projects]);

  // 既定では完了済みプロジェクトを隠す。トグルON、または完了済みチップ選択時は表示。
  const visible = useMemo(
    () => (includeArchived || statusFilter === 'finished'
      ? projects : projects.filter((p) => !p.is_finished)),
    [projects, includeArchived, statusFilter]
  );

  // Client-side search (backend already handled include_archived / eid filter).
  const filtered = useMemo(() => {
    const nq = (q || '').trim().toLowerCase();
    return visible.filter((p) => {
      if (statusFilter !== 'all' && !matchesChip(p, statusFilter)) return false;
      if (!nq) return true;
      const bag = `${p.name || ''} ${p.description || ''} ${p.department_name || ''} ${p.owner_name || ''}`.toLowerCase();
      return bag.includes(nq);
    }).sort(ganttOrder((p) => p.end_date, (p) => p.start_date));
  }, [visible, statusFilter, q]);

  // Chip counts come from the FULL list, never from `visible` — otherwise the
  // 完了済み count depends on the 完了も表示 toggle and reads 0 while projects
  // are finished. Search still applies, so counts track what the user typed.
  const searchOnly = useMemo(() => {
    const nq = (q || '').trim().toLowerCase();
    if (!nq) return projects;
    return projects.filter((p) => `${p.name || ''} ${p.description || ''} ${p.department_name || ''} ${p.owner_name || ''}`.toLowerCase().includes(nq));
  }, [projects, q]);
  const counts = useMemo(() => {
    // すべて excludes finished unless they are being shown, so it matches the
    // list underneath it rather than the chip sum.
    const c = { all: searchOnly.filter((p) => includeArchived || !p.is_finished).length };
    PROJECT_FILTERS.forEach((s) => { c[s] = searchOnly.filter((p) => matchesChip(p, s)).length; });
    return c;
  }, [searchOnly, includeArchived]);

  const openCreate = () => setFormOpen(true);
  const onSaved = (created) => {
    load();
    ctx?.reloadSidebar?.();
    if (created?.id) {
      toast.success(t('planner.saved'));
      // navigate straight into the new project board for the classic feel
      setTimeout(() => navigate(`/planner/project/${created.id}`), 60);
    }
  };

  if (loading && !data?.projects?.length) {
    return <div className="py-24 text-center text-sm text-zinc-400" data-testid="myproj-loading">…</div>;
  }

  if (data.employee_linked === false) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 text-center max-w-md mx-auto" data-testid="myproj-not-linked">
        <UserCircle size={40} weight="duotone" className="text-amber-500 mx-auto mb-2" />
        <p className="text-sm text-amber-800">{t('planner.myProjects.notLinked')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="planner-my-projects">
      {/* Title row + Create button */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900">{t('planner.myProjects.title')}</h1>
          <p className="text-sm text-zinc-500 mt-1">{t('planner.myProjects.subtitle')}</p>
        </div>
        <Button
          onClick={openCreate}
          data-testid="myproj-new-btn"
          className="bg-teal-700 hover:bg-teal-800"
        >
          <Plus size={18} weight="bold" className="mr-1" /> {t('planner.project.new')}
        </Button>
      </div>

      {/* Search + archive toggle */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" weight="bold" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t('planner.myProjects.searchPh')}
            className="h-10 pl-8"
            data-testid="myproj-search"
          />
        </div>
        <div className="inline-flex rounded-lg border border-zinc-200 bg-white p-0.5 h-10" data-testid="myproj-view-toggle">
          <button
            type="button"
            onClick={() => switchView('cards')}
            data-testid="myproj-view-cards"
            className={`inline-flex items-center gap-1.5 px-3 rounded-md text-xs font-medium transition-colors ${
              view === 'cards' ? 'bg-indigo-600 text-white shadow-sm' : 'text-zinc-500 hover:bg-zinc-50'
            }`}
          >
            <SquaresFour size={14} weight={view === 'cards' ? 'fill' : 'regular'} /> {t('planner.view.cards')}
          </button>
          <button
            type="button"
            onClick={() => switchView('gantt')}
            data-testid="myproj-view-gantt"
            className={`inline-flex items-center gap-1.5 px-3 rounded-md text-xs font-medium transition-colors ${
              view === 'gantt' ? 'bg-indigo-600 text-white shadow-sm' : 'text-zinc-500 hover:bg-zinc-50'
            }`}
          >
            <ChartBar size={14} weight={view === 'gantt' ? 'fill' : 'regular'} /> {t('planner.view.gantt')}
          </button>
        </div>
        <button
          type="button"
          onClick={() => setIncludeArchived((v) => !v)}
          data-testid="myproj-toggle-archived"
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 h-10 rounded-lg border text-xs font-medium transition-colors ${
            includeArchived ? 'bg-zinc-800 text-white border-zinc-800' : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50'
          }`}
        >
          <Archive size={14} weight={includeArchived ? 'fill' : 'regular'} />
          {t('planner.myProjects.showArchived')}
        </button>
      </div>

      {/* Status chip row */}
      <div className="flex items-center gap-2 flex-wrap">
        <StatusChip value="all" count={counts.all} active={statusFilter === 'all'} onClick={() => setStatusFilter('all')} label={t('planner.myProjects.filterAll')} />
        {PROJECT_FILTERS.map((s) => (
          <StatusChip key={s} value={s} count={counts[s] || 0} active={statusFilter === s} onClick={() => setStatusFilter(s)} label={t(`planner.projectStatus.${s}`)} />
        ))}
        <div className="ml-auto text-xs text-zinc-400 tabular-nums">{t('planner.count', { n: filtered.length })}</div>
      </div>

      {/* Error state */}
      {err && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 text-sm text-rose-700" data-testid="myproj-error">
          {err}
        </div>
      )}

      {/* Empty state */}
      {!err && filtered.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-zinc-300 p-10 text-center" data-testid="myproj-empty">
          <Kanban size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
          <p className="text-sm text-zinc-500">{t('planner.myProjects.empty')}</p>
          <p className="text-xs text-zinc-400 mt-1">{t('planner.myProjects.emptyHint')}</p>
          <Button onClick={openCreate} variant="outline" className="mt-3 border-indigo-200 text-indigo-600 hover:bg-indigo-50" data-testid="myproj-empty-new-btn">
            <Plus size={16} className="mr-1" />{t('planner.project.new')}
          </Button>
        </div>
      ) : view === 'gantt' ? (
        <div data-testid="myproj-gantt-wrap">
          <GanttChart
            items={filtered.map((p) => ({
              id: p.id,
              label: p.name,
              sub: `${p.department_name ? p.department_name + ' · ' : ''}${t('planner.gantt.tasks', { done: p.done_count, total: p.task_count })}`,
              start: p.start_date,
              end: p.end_date,
              color: PRIORITY_BAR[p.priority] || '#4F46E5',
              priority: p.priority,
            }))}
            onItemClick={(it) => navigate(`/planner/project/${it.id}`)}
            emptyText={t('planner.myProjects.empty')}
          />
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children" data-testid="myproj-grid">
          {filtered.map((p) => (
            <ProjectCard key={p.id} project={p} role={roleOf(p)} showDept
              onOpen={() => navigate(`/planner/project/${p.id}`)}
              testid={`myproj-card-${p.id}`} statusTestId={`my-project-status-${p.id}`}
              deptTestId={`myproj-dept-${p.id}`} roleTestId={`myproj-role-${p.id}`} />
          ))}
        </div>
      )}

      {/* Create dialog — no preselected department, picker shown inside */}
      <ProjectFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        departmentId={null}
        people={people}
        onSaved={onSaved}
      />
    </div>
  );
}
